import { Injectable } from '@angular/core';
import { Locale } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AccountProviderService,
  ConfirmationPreferenceTypes,
  DeviceScrollbarVisibility,
  OnboardingConfigFlowTypes,
  PaymentProvider,
  ProjectAuthenticationSettings,
  ProjectConfiguration,
  RelevantBaasTags,
  TransitionPresetType,
  UISettings,
  defaultAispGocardlessFlowRedirectionConfig,
  defaultAuthoriseFlowConfirmationPreferences,
  defaultConfirmationPreferences,
  defaultPostDownloadRedirectionConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import jwt_decode from 'jwt-decode';
import { catchError, tap } from 'rxjs';
import {
  AddProgressBarWorkflowStack,
  RemoveProgressBarWorkflowStack,
} from '../actions/progress-bar-workflow.action';
import { GetProjectAlerts } from '../actions/project-alerts.action';
import {
  PublishProjectWorkflowConfig,
  SetActiveProject,
  UpdateProjectAlertToaster,
} from '../actions/project.action';
import { ProjectExperienceKind } from '../dtos/code-projects.dto';
import { ApiService } from '../services/api-service/api.service';

export interface ProjectsStateModel {
  active: {
    id: string;
    name: string;
    paymentGateway: PaymentProvider;
    sandboxMode: string;
    supportedCurrency: string[];
    baasConfiguration: AccountProviderService;
    onboardingFlow: OnboardingConfigFlowTypes;
    defaultCurrency: string;
    relevantBaasTags: RelevantBaasTags[];
    checkPayeeEnabled: boolean;
    smsAuthenticationEnabled: boolean;
    smsAuthenticationExclusion: { [key: string]: string[] };
    enableBeneficiaryFirstAndLastName: boolean;
    serviceProvider: string;
    defaultOtherAlerts: boolean;
    uiSettings?: UISettings;
    multiOrganisationPaymentApproval?: boolean;
    confirmationPreferenceOrder: ConfirmationPreferenceTypes[];
    authentication?: ProjectAuthenticationSettings;
    projectConfig?: ProjectConfiguration;
    deviceScrollbarVisibility?: DeviceScrollbarVisibility;
    experienceKind?: ProjectExperienceKind;
    appStudioEnabled?: boolean;
  };
}
export interface ActiveProjectInterface {
  id: string;
  name: string;
  paymentGateway: PaymentProvider;
  sandboxMode: string;
  supportedCurrency: string[];
  baasConfiguration: AccountProviderService;
  onboardingFlow: OnboardingConfigFlowTypes;
  defaultCurrency: string;
  checkPayeeEnabled: boolean;
  smsAuthenticationEnabled: boolean;
  smsAuthenticationExclusion: { [key: string]: string[] };
  enableBeneficiaryFirstAndLastName: boolean;
  defaultOtherAlerts: boolean;
  uiSettings?: UISettings;
  multiOrganisationPaymentApproval?: boolean;
  confirmationPreferenceOrder: ConfirmationPreferenceTypes[];
  authentication?: ProjectAuthenticationSettings;
  projectConfig?: ProjectConfiguration;
  deviceScrollbarVisibility?: DeviceScrollbarVisibility;
  /** The serving experience stays configured while a legacy App Studio workspace is prepared. */
  experienceKind?: ProjectExperienceKind;
  appStudioEnabled?: boolean;
}
export interface WorkflowProjectType {
  id: string;
  name: string;
  paymentGateway: PaymentProvider;
  sandboxMode: string;
  baasConfiguration: AccountProviderService;
  onboardingFlow: OnboardingConfigFlowTypes;
  defaultCurrency: string;
  relevantBaasTags: RelevantBaasTags[];
  checkPayeeEnabled: boolean;
  smsAuthenticationEnabled: boolean;
  smsAuthenticationExclusion: { [key: string]: string[] };
  enableBeneficiaryFirstAndLastName: boolean;
  serviceProvider: string;
  defaultOtherAlerts: boolean;
  uiSettings?: UISettings;
  multiOrganisationPaymentApproval?: boolean;
  confirmationPreferenceOrder: ConfirmationPreferenceTypes[];
  authentication?: ProjectAuthenticationSettings;
  projectConfig?: ProjectConfiguration;
  // supportedCurrency: string[];
  deviceScrollbarVisibility?: DeviceScrollbarVisibility;
}

@State<ProjectsStateModel>({
  name: 'workflowProjects',
  defaults: {
    active: {
      id: '',
      name: '',
      paymentGateway: PaymentProvider.MODULR,
      sandboxMode: '',
      onboardingFlow: '' as OnboardingConfigFlowTypes,
      supportedCurrency: [],
      relevantBaasTags: [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      baasConfiguration: {} as any,
      defaultCurrency: '',
      checkPayeeEnabled: true,
      smsAuthenticationEnabled: true,
      smsAuthenticationExclusion: {},
      enableBeneficiaryFirstAndLastName: false,
      serviceProvider: '',
      defaultOtherAlerts: true,
      uiSettings: {
        desktop: {
          modalTransition: '',
          pageTransition: {} as TransitionPresetType,
        },
        mobile: {
          modalTransition: '',
          pageTransition: {} as TransitionPresetType,
        },
        tablet: {
          pageTransition: {} as TransitionPresetType,
          modalTransition: '',
        },
      },
      multiOrganisationPaymentApproval: false,
      confirmationPreferenceOrder: defaultConfirmationPreferences,
      authentication: defaultAuthoriseFlowConfirmationPreferences,
      projectConfig: {
        aispFlowRedirectionConfig: defaultAispGocardlessFlowRedirectionConfig,
        postDownloadRedirectionConfig: defaultPostDownloadRedirectionConfig,
      } as ProjectConfiguration,
      deviceScrollbarVisibility: {
        mobile: false,
        tablet: false,
        desktop: false,
      },
      experienceKind: 'configured',
    },
  },
  children: [],
})
@Injectable()
export class ProjectsState {
  constructor(
    private store: Store,
    private apiService: ApiService,
    private alertHandlerService: AlertHandlerService,
  ) {}

  @Selector()
  static getProjectsState(state: ProjectsStateModel) {
    return state;
  }

  @Selector()
  static getActiveProject(state: ProjectsStateModel) {
    return state.active;
  }

  @Action(SetActiveProject)
  setActiveProject(ctx: StateContext<ProjectsStateModel>, action: SetActiveProject) {
    return ctx.patchState({
      active: {
        name: action.payload.name,
        id: action.payload.id,
        paymentGateway: action.payload.paymentGateway,
        sandboxMode: action.payload.sandboxMode,
        supportedCurrency: action.payload.supportedCurrency,
        baasConfiguration: action.payload.baasConfiguration,
        onboardingFlow: action.payload.onboardingFlow,
        defaultCurrency: action.payload.defaultCurrency,
        relevantBaasTags: this.parseRelevantBaasTags(action.payload.baasConfiguration),
        checkPayeeEnabled: action.payload.checkPayeeEnabled,
        smsAuthenticationEnabled: action.payload.smsAuthenticationEnabled,
        smsAuthenticationExclusion: action.payload?.smsAuthenticationExclusion,
        enableBeneficiaryFirstAndLastName: action.payload.enableBeneficiaryFirstAndLastName,
        serviceProvider: this.getServiceProvider(action.payload.baasConfiguration),
        defaultOtherAlerts: action.payload.defaultOtherAlerts,
        uiSettings: action.payload.uiSettings,
        multiOrganisationPaymentApproval: action.payload.multiOrganisationPaymentApproval,
        confirmationPreferenceOrder: action.payload.confirmationPreferenceOrder,
        authentication: action.payload?.authentication,
        projectConfig: {
          ...action.payload.projectConfig,
          aispFlowRedirectionConfig:
            action.payload.projectConfig?.aispFlowRedirectionConfig ||
            defaultAispGocardlessFlowRedirectionConfig,
          postDownloadRedirectionConfig:
            action.payload.projectConfig?.postDownloadRedirectionConfig ||
            defaultPostDownloadRedirectionConfig,
        } as ProjectConfiguration,
        deviceScrollbarVisibility: {
          mobile: action.payload?.deviceScrollbarVisibility?.mobile ?? false,
          tablet: action.payload?.deviceScrollbarVisibility?.tablet ?? false,
          desktop: action.payload?.deviceScrollbarVisibility?.desktop ?? false,
        },
        experienceKind: action.payload?.experienceKind ?? 'configured',
        appStudioEnabled: action.payload?.appStudioEnabled ?? false,
      },
    });
  }

  private parseRelevantBaasTags(baasConfiguration: AccountProviderService): RelevantBaasTags[] {
    // relevant banking as a service provider tags
    // i.e. add paymentProvider and etra if specific info in the below example
    const baaStags: string[] = [baasConfiguration.provider];
    if (baasConfiguration.provider === PaymentProvider.INTEGRATED_FINANCE) {
      if (baasConfiguration?.integratedFinanceConfig?.serviceProvider) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        baaStags.push('if-' + baasConfiguration.integratedFinanceConfig.serviceProvider.toLowerCase());
      }
    }

    return baaStags as RelevantBaasTags[];
  }

  /**
   * Retrieves the service provider from the given BaaS configuration.
   *
   * @param baasConfiguration - The account provider service configuration.
   * @returns The service provider name if available; otherwise, an empty string.
   */
  private getServiceProvider(baasConfiguration: AccountProviderService): string {
    // Check if the provider is Integrated Finance
    if (baasConfiguration.provider === PaymentProvider.INTEGRATED_FINANCE) {
      // Return the service provider from the integrated finance configuration, if available
      return baasConfiguration?.integratedFinanceConfig?.serviceProvider || '';
    }

    // Return an empty string if the provider is not Integrated Finance or no service provider is found
    return '';
  }

  @Action(PublishProjectWorkflowConfig)
  publishProjectConfig(ctx: StateContext<ProjectsStateModel>, action: PublishProjectWorkflowConfig) {
    const alertHandlerService = action.injector.get(AlertHandlerService);
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;

    const requestBody = {
      name: action.payload?.name ? action.payload.name : undefined,
      tenant: this.getOrgId(),
      projectId: projectId,
    };
    this.apiService
      .postCall('/create-update-system-config', requestBody)
      .pipe(
        tap((response) => {
          if (response == true) {
            alertHandlerService.showAlertFn('success', 'Voilà..!!');
          }
        }),
        catchError((err) => {
          alertHandlerService.showAlertFn('error', 'Failed to publish config');
          throw new Error(err);
        }),
      )
      .subscribe();
  }

  private getOrgId(): string {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const jwt = jwt_decode(sessionStorage.getItem('token')!) as any;
    const organisation = jwt.organisation;

    return Object.keys(organisation)[0];
  }

  @Action(UpdateProjectAlertToaster)
  updateProjectAlertToaster(ctx: StateContext<ProjectsStateModel>, action: UpdateProjectAlertToaster) {
    ctx.dispatch(new AddProgressBarWorkflowStack({ uniqueId: 'UpdateProjectAlertToaster' }));
    this.apiService
      .updateProjectAlertToaster(action.payload)
      .pipe(
        tap(() => {
          ctx.dispatch(
            new RemoveProgressBarWorkflowStack({
              uniqueId: 'UpdateProjectAlertToaster',
            }),
          );
          const apiParams = {
            locale: Locale.en_GB,
            key: 'alerts',
          };
          ctx.dispatch(new GetProjectAlerts(apiParams, action.payload.projectId));
          this.alertHandlerService.showAlertFn('success', 'Alert toaster has been updated.');
        }),
        catchError((err) => {
          ctx.dispatch(
            new RemoveProgressBarWorkflowStack({
              uniqueId: 'UpdateProjectAlertToaster',
            }),
          );
          this.alertHandlerService.showAlertFn('error', 'Failed to update the alert toaster.');
          throw new Error(err);
        }),
      )
      .subscribe();
  }
}
